import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  IconHome2,
  IconHomePlus,
  IconShare,
  IconTimeline,
  IconActivity,
  IconTrash,
  IconUserCheck,
  IconClock,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils/cn";
import { listProperties, revokeAssignment } from "../api";
import type { AgencyProperty } from "../api";
import { CreatePropertyModal } from "../components/CreatePropertyModal";
import { DistributePropertyModal } from "../components/DistributePropertyModal";
import { PropertyCycleDrawer } from "../components/PropertyCycleDrawer";

/** Parado: 15+ dias sem sinal de vida. Nunca movido é caso à parte — não é "0 dias". */
function isStalled(daysWithoutActivity: number | null) {
  return daysWithoutActivity === null || daysWithoutActivity >= 15;
}

/**
 * Imóveis da casa (hub#689): o que a imobiliária cadastrou e para quem distribuiu. A coluna
 * que importa é "com quem está" — é ela que responde quem está sentado no imóvel hoje.
 */
export function PropertiesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [distributing, setDistributing] = useState<AgencyProperty | null>(null);
  const [inspecting, setInspecting] = useState<AgencyProperty | null>(null);

  const { data: properties, isLoading } = useQuery({
    queryKey: ["agency-properties"],
    queryFn: listProperties,
  });

  const revoke = useMutation({
    mutationFn: revokeAssignment,
    onSuccess: () => {
      toast.success(t("properties.toast.revoked"));
      queryClient.invalidateQueries({ queryKey: ["agency-properties"] });
    },
    onError: () => toast.error(t("properties.errors.default")),
  });

  const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold tracking-tight text-ink">{t("properties.title")}</h2>
          <p className="text-ink-muted">{t("properties.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-grad px-5 py-2.5 text-sm font-black uppercase tracking-wider text-white shadow-soft transition-all hover:scale-105 hover:shadow-glow active:scale-95"
        >
          <IconHomePlus size={18} />
          {t("properties.new")}
        </button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-ink-soft">{t("properties.loading")}</p>
      ) : !properties || properties.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-white/40 p-12 text-center backdrop-blur-sm">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-300">
            <IconHome2 size={32} />
          </div>
          <h3 className="mb-2 text-lg font-bold text-ink">{t("properties.empty.title")}</h3>
          <p className="max-w-sm text-sm leading-relaxed text-ink-muted">
            {t("properties.empty.desc")}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {properties.map((property) => (
            <article
              key={property.id}
              className="rounded-3xl border border-white/40 bg-white/70 p-6 shadow-card backdrop-blur-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-ink">{property.title}</h3>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-soft">
                      {property.internalCode}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-ink-muted">
                    {[property.neighborhood, property.city].filter(Boolean).join(" · ")}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <p className="text-lg font-black text-ink">
                      {property.salePrice
                        ? money.format(property.salePrice)
                        : property.rentPrice
                          ? `${money.format(property.rentPrice)}${t("properties.perMonth")}`
                          : "—"}
                    </p>
                    {/* O selo do pulso vem na lista: é o que faz a imobiliária agir sem
                        precisar abrir imóvel por imóvel (hub#690). */}
                    {property.activity && (
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider",
                          isStalled(property.activity.daysWithoutActivity)
                            ? "bg-warning-soft text-warning"
                            : "bg-gray-100 text-ink-soft",
                        )}
                      >
                        <IconActivity size={12} />
                        {property.activity.daysWithoutActivity === null
                          ? t("properties.cycle.neverMoved")
                          : t("properties.cycle.daysWithout", {
                              count: property.activity.daysWithoutActivity,
                            })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setInspecting(property)}
                    className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-brand-300 hover:bg-brand-grad-soft"
                  >
                    <IconTimeline size={16} />
                    {t("properties.cycleAction")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDistributing(property)}
                    className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-brand-300 hover:bg-brand-grad-soft"
                  >
                    <IconShare size={16} />
                    {t("properties.distributeAction")}
                  </button>
                </div>
              </div>

              <div className="mt-5 border-t border-border-soft pt-4">
                <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-ink-soft">
                  {t("properties.withWhom")}
                </p>
                {property.assignments.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t("properties.notDistributed")}</p>
                ) : (
                  <ul className="grid gap-2">
                    {property.assignments.map((assignment) => (
                      <li
                        key={assignment.id}
                        className="flex flex-wrap items-center gap-3 rounded-xl bg-gray-50/60 px-3 py-2"
                      >
                        {assignment.status === "pending" ? (
                          <IconClock size={16} className="text-warning" />
                        ) : (
                          <IconUserCheck size={16} className="text-success" />
                        )}
                        <span className="text-sm font-bold text-ink">{assignment.brokerName}</span>
                        <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                          {t(`brokers.linkType.${assignment.linkType}`)}
                          {" · "}
                          {t(`properties.distribute.mode.${assignment.mode}`)}
                          {assignment.commissionPercentage
                            ? ` · ${assignment.commissionPercentage}%`
                            : ""}
                        </span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider",
                            assignment.status === "pending"
                              ? "bg-warning-soft text-warning"
                              : "bg-success-soft text-success",
                          )}
                        >
                          {t(`properties.assignmentStatus.${assignment.status}`)}
                        </span>
                        <button
                          type="button"
                          onClick={() => revoke.mutate(assignment.id)}
                          disabled={revoke.isPending}
                          aria-label={t("properties.revoke")}
                          className="ml-auto rounded-lg p-1.5 text-ink-soft transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                        >
                          <IconTrash size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <CreatePropertyModal open={creating} onOpenChange={setCreating} />
      <DistributePropertyModal property={distributing} onOpenChange={() => setDistributing(null)} />
      <PropertyCycleDrawer property={inspecting} onOpenChange={() => setInspecting(null)} />
    </div>
  );
}
